/**
 * Every backend path used by the frontend, in one place.
 *
 * SOURCE OF TRUTH: the current Spring Boot controllers (responsily-api). The
 * backend is being migrated to Express/TypeScript and its final contract is
 * UNKNOWN. If a path or verb changes, change it here only; components and
 * services never build URLs themselves.
 *
 * Path segment casing mirrors the Spring controllers exactly (e.g.
 * `targetGroup`, `QuotaSetting`), because Spring routes are case-sensitive.
 */
const enc = encodeURIComponent;

export const endpoints = {
  auth: {
    login: () => '/login/auth', // POST {username,password}
    me: () => '/users/me', // GET -> the signed-in user (with role); 401 without a token
    users: () => '/users', // GET -> users of my organisation (admin; superadmin sees all); POST add a user (admin)
    register: () => '/login/register', // POST {username,password,firstName?,lastName?,email?,mobile?} -> 201 user (no password)
  },
  projects: {
    all: () => '/project/all', // GET -> Project[]
    byStatus: (status, page, size) => `/project/${enc(status)}/${page}/${size}`, // GET -> Spring Page (1-based page)
    byId: (id) => `/project/${enc(id)}`, // GET -> Project | empty body
    create: () => '/project/create', // POST Project
    update: (id) => `/project/update/${enc(id)}`, // PATCH {projectCategoryId,targetGroups,targetGrouplist}
    remove: (id) => `/project/delete/${enc(id)}`, // DELETE
    statistics: (id) => `/project/${enc(id)}/statistics`, // GET -> field statistics (counts, wanted, remaining, rates in %)
    status: (id) => `/project/${enc(id)}/status`, // PATCH { projectStatus: 'active' | 'draft' | 'closed' }
    timeline: (id) => `/project/${enc(id)}/statistics/timeline`, // GET ?interval=day|week|month&from=&to=&tz=&targetGroupId= -> { projectId, source, interval, tz, points[] }
    respondents: (id) => `/project/${enc(id)}/respondents`, // GET ?status=&targetGroupId=&page=1&size=50 -> { content, totalElements, page, size, totalPages }
    respondentsCsv: (id) => `/project/${enc(id)}/respondents.csv`, // GET (same filters) -> CSV
    reconcile: (id) => `/project/${enc(id)}/respondents/reconciliations`, // POST [{ id, status, reason? }] -> { updated, failed, results[] }
    readiness: (id) => `/project/${enc(id)}/readiness`, // GET -> { ready, problems, warnings }
    pricing: (id) => `/project/${enc(id)}/pricing`, // GET -> per target group rate and estimated cost, totals, basis
    redirectStats: (id) => `/project/${enc(id)}/redirect-stats`, // GET -> { complete, screenout, quotaFull, total }
    search: () => '/project/search', // GET ?name=&id=
  },
  categories: {
    all: () => '/project/category/all', // GET -> Category[]
  },
  targetGroups: {
    create: (projectId) => `/project/${enc(projectId)}/targetGroup/create`, // POST
    update: (projectId, tgId) => `/project/${enc(projectId)}/targetGroup/${enc(tgId)}`, // PATCH { name?, surveyUrl? (null = use the campaign's) }
    basicSettings: (projectId, tgId) =>
      `/project/${enc(projectId)}/targetGroup/${enc(tgId)}/basicsetting/create`, // POST BasicSettings -> TargetGroup
    quota: (projectId, tgId) =>
      `/project/${enc(projectId)}/targetGroup/${enc(tgId)}/QuotaSetting/create`, // POST QuotaSettings -> TargetGroup
    region: (projectId, tgId) =>
      `/project/${enc(projectId)}/targetGroup/${enc(tgId)}/region`, // POST RegionSettings -> TargetGroup
    profiling: (projectId, tgId) =>
      `/project/${enc(projectId)}/targetGroup/${enc(tgId)}/profiling`, // POST [answerId, ...] (replaces; [] clears) -> TargetGroup
    excluded: (projectId, tgId) =>
      `/project/${enc(projectId)}/targetGroup/${enc(tgId)}/excluded-projects`, // POST [projectId, ...] (replaces; [] clears) -> TargetGroup
    applyQuotaPreset: (projectId, tgId, presetId) =>
      `/project/${enc(projectId)}/targetGroup/${enc(tgId)}/apply-quota-preset/${enc(presetId)}`, // POST (no body) -> TargetGroup
    applyTemplate: (projectId, tgId, templateId) =>
      `/project/${enc(projectId)}/targetGroup/${enc(tgId)}/apply-template/${enc(templateId)}`, // POST (no body) -> TargetGroup
  },
  feasibility: {
    calculate: () => '/feasibility', // POST FeasibilityInput -> FeasibilityResult (works with unsaved values)
    saved: (projectId, tgId) => `/project/${enc(projectId)}/targetGroup/${enc(tgId)}/feasibility`, // GET -> FeasibilityResult for the saved target group
  },
  quotaPresets: {
    all: () => '/quota-presets', // GET ?clientId= -> QuotaPreset[]; POST QuotaPreset
    byId: (id) => `/quota-presets/${enc(id)}`, // GET / PUT / DELETE
    fromTargetGroup: (tgId) => `/quota-presets/from-target-group/${enc(tgId)}`, // POST { name, description, clientId } -> QuotaPreset
  },
  templates: {
    all: () => '/targetgroup-templates', // GET ?clientId= -> Template[]; POST Template
    byId: (id) => `/targetgroup-templates/${enc(id)}`, // DELETE
    fromTargetGroup: (tgId) => `/targetgroup-templates/from-target-group/${enc(tgId)}`, // POST { name, description, clientId } -> Template
  },
  reports: {
    campaigns: () => '/reports/campaigns', // GET ?status=&search=&page=1&size=20&from=&to=&tz= -> { content, page, size, totalElements, totalPages, filters, totals }
  },
  reference: {
    responseStatuses: () => '/reference/response-statuses', // GET -> [{ key|status, description }]
  },
  geo: {
    countries: () => '/countries',
    states: (countryId) => `/countries/${enc(countryId)}/states`,
    cities: (countryId, stateId) => `/countries/${enc(countryId)}/states/${enc(stateId)}/cities`,
  },
  rateCard: {
    price: () => '/survey/ratecard/price', // GET ?ir=&loi= -> number (a percentage, see README)
  },
  profiling: {
    categories: () => '/global_profiling/get_all_categories', // categories with questions and answers embedded
  },
};
